import { useCallback, useEffect, useMemo, useState } from 'react';
const clamp = (n, min, max) => Math.min(Math.max(n, min), max);
const parseStepHash = () => {
    if (typeof window === 'undefined')
        return null;
    const hash = window.location.hash;
    const match = hash.match(/#?step-(\d+)-(\d+)/);
    if (!match)
        return null;
    const pageNumber = Math.max(1, parseInt(match[1], 10));
    const stepNumber = Math.max(1, parseInt(match[2], 10));
    return [pageNumber - 1, stepNumber - 1];
};
export function useStepHashNavigation(params) {
    const { pageIds, displayedStepsByPageId, allSteps } = params;
    const computeInitialIndex = useCallback(() => {
        const tuple = parseStepHash();
        if (!tuple)
            return 0;
        const [targetPageIndex, targetStepIndex] = tuple;
        if (targetPageIndex < 0 || targetPageIndex >= pageIds.length)
            return 0;
        let globalIndex = 0;
        for (let pageIndex = 0; pageIndex < pageIds.length; pageIndex++) {
            if (pageIndex === targetPageIndex)
                break;
            const pageId = pageIds[pageIndex];
            const stepsInPage = displayedStepsByPageId[pageId] || [];
            globalIndex += stepsInPage.length;
        }
        const targetPageId = pageIds[targetPageIndex];
        const stepsInTargetPage = displayedStepsByPageId[targetPageId] || [];
        if (targetStepIndex < 0 || targetStepIndex >= stepsInTargetPage.length) {
            return 0;
        }
        const maxIndex = Math.max(allSteps.length - 1, 0);
        return clamp(globalIndex + targetStepIndex, 0, maxIndex);
    }, [pageIds, displayedStepsByPageId, allSteps.length]);
    const [currentStepIndex, setCurrentStepIndex] = useState(computeInitialIndex());
    useEffect(() => {
        setCurrentStepIndex((i) => clamp(i, 0, Math.max(allSteps.length - 1, 0)));
    }, [allSteps.length]);
    useEffect(() => {
        if (typeof window === 'undefined')
            return;
        const entry = allSteps[currentStepIndex];
        if (!entry)
            return;
        const tuple = parseStepHash();
        const isTupleValid = (() => {
            if (!tuple)
                return false;
            const [p, s] = tuple;
            if (p < 0 || p >= pageIds.length)
                return false;
            const pageId = pageIds[p];
            const stepsInPage = displayedStepsByPageId[pageId] || [];
            return s >= 0 && s < stepsInPage.length;
        })();
        const id = `step-${entry.pageIndex + 1}-${entry.stepIndexInPage + 1}`;
        const desired = `#${id}`;
        const currentHash = window.location.hash;
        if (!isTupleValid && currentHash !== desired) {
            window.location.hash = id;
        }
    }, [currentStepIndex, allSteps, pageIds, displayedStepsByPageId]);
    const setUrlHashForIndex = useCallback((index) => {
        if (typeof window === 'undefined')
            return;
        const entry = allSteps[index];
        if (!entry)
            return;
        const id = `step-${entry.pageIndex + 1}-${entry.stepIndexInPage + 1}`;
        const desired = `#${id}`;
        if (window.location.hash !== desired) {
            window.location.hash = id;
        }
    }, [allSteps]);
    const goPrev = useCallback(() => {
        setCurrentStepIndex((i) => {
            const next = clamp(i - 1, 0, Math.max(allSteps.length - 1, 0));
            setUrlHashForIndex(next);
            return next;
        });
    }, [allSteps.length, setUrlHashForIndex]);
    const goNext = useCallback(() => {
        setCurrentStepIndex((i) => {
            const next = clamp(i + 1, 0, Math.max(allSteps.length - 1, 0));
            setUrlHashForIndex(next);
            return next;
        });
    }, [allSteps.length, setUrlHashForIndex]);
    const goToStepIndex = useCallback((index) => {
        setCurrentStepIndex(() => {
            const next = clamp(index, 0, Math.max(allSteps.length - 1, 0));
            setUrlHashForIndex(next);
            return next;
        });
    }, [allSteps.length, setUrlHashForIndex]);
    const currentEntry = useMemo(() => allSteps[currentStepIndex], [allSteps, currentStepIndex]);
    return {
        currentStepIndex,
        currentEntry,
        setCurrentStepIndex,
        goPrev,
        goNext,
        goToStepIndex,
    };
}
